/**
 * StatTile — one number an operator scans, not reads.
 *
 * The dashboard's top strip is the first thing anyone looks at in the morning and the
 * last thing they check before going home. It has to survive being screenshotted into a
 * ticket, printed in greyscale, and read by someone who does not distinguish red from
 * green — so every tile carries its state three ways:
 *
 *   1. a glyph  (✓ ▲ ✕ ◷ ? ·) — survives greyscale
 *   2. a border treatment — dashed while a thing is still moving, double when it is a
 *      terminal failure (the same grammar Badge.tsx uses)
 *   3. colour, last, and never on its own
 *
 * The border is EVEN on all four sides. It used to carry a 3px coloured left edge over a
 * tinted fill — the exact shape the accent-bar guardrail bans — and it shipped because the
 * check matched CSS syntax and could not see a camelCased `borderLeftWidth` in a React
 * style object. `.toast` in styles.css solves the same problem the right way: tint the
 * whole border, keep the glyph, and let the chip do the encoding.
 *
 * ONE ROW, about 40px. Seven 100px tiles wrapped to two rows and pushed "Needs attention"
 * — the table that says what is wrong — below the fold on the screen whose whole design
 * goal is a four-second answer. Label and glyph read left, the number reads right in
 * tabular numerals, and the context that used to be a third line is the tile's tooltip.
 *
 * "We do not know" is renderable, and it does not look like "all clear". The dashboard
 * loads with Promise.allSettled and keeps whatever resolved, so a half-failed fetch used
 * to render an authoritative grey `0` — the most trusted thing on the screen, fabricated.
 * Pass `unknown` the sentence naming the fetch that failed, and the tile renders `—` with
 * a `?` and says so on hover. The reason IS the prop, so a tile cannot be marked unknown
 * without one.
 *
 * A tile with nothing wrong is deliberately quiet: pass tone="muted" (or use `countTone`)
 * for a zero count so the only coloured things on screen are the ones that want a human.
 * Six red tiles reading "0" train people to ignore red.
 *
 * Styling is inline because styles.css belongs to the shell and this file does not own
 * it — every value here is a design token, so light/dark and the theme toggle work
 * without a single new CSS rule.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { CSSProperties, ReactNode } from 'react';

export type StatTone = 'default' | 'ok' | 'warn' | 'danger' | 'info' | 'accent' | 'muted';

interface ToneSpec {
  ink: string;
  line: string;
  soft: string;
  glyph: string;
}

const TONES: Record<StatTone, ToneSpec> = {
  default: { ink: 'var(--text)', line: 'var(--line-strong)', soft: 'var(--surface-2)', glyph: '·' },
  ok: { ink: 'var(--ok)', line: 'var(--ok-line)', soft: 'var(--ok-soft)', glyph: '✓' },
  warn: { ink: 'var(--warn)', line: 'var(--warn-line)', soft: 'var(--warn-soft)', glyph: '▲' },
  danger: { ink: 'var(--danger)', line: 'var(--danger-line)', soft: 'var(--danger-soft)', glyph: '✕' },
  info: { ink: 'var(--info)', line: 'var(--info-line)', soft: 'var(--info-soft)', glyph: '◷' },
  accent: { ink: 'var(--accent)', line: 'var(--accent-line)', soft: 'var(--accent-soft)', glyph: '◆' },
  muted: { ink: 'var(--text-3)', line: 'var(--line)', soft: 'var(--surface-2)', glyph: '·' },
};

interface StatTileBase {
  /** Short noun phrase: "Stale", "Links expiring", "Runs today". */
  label: string;
  /** Already formatted — formatCount / formatBytes / a badge. Never a raw Number. */
  value: ReactNode;
  /** The context that used to be a third line. Goes in the tooltip, beside `title`. */
  hint?: string;
  tone?: StatTone;
  /** Override the tone's glyph. Pass null to drop it (rare — it is the greyscale channel). */
  glyph?: string | null;
  /** Internal route. Makes the whole tile a link, keyboard included. */
  to?: string;
  /** Alternative to `to` for in-page actions (scrolling to the section below, say). */
  onClick?: () => void;
  /** Shimmer in place of the value while the first fetch is in flight. */
  loading?: boolean;
  /** Dashed border: this number is still moving (runs in flight). */
  pending?: boolean;
  /** Double border: terminal failure that will not resolve itself. */
  terminalBad?: boolean;
  /** Native tooltip. Say what the number counts and over what window. */
  title?: string;
  /**
   * The count could not be measured — pass the SENTENCE saying which fetch failed, and
   * the tile renders `—` with a `?` instead of a number, and says so on hover.
   *
   * It is a reason, not a boolean, on purpose: a tile that says "we could not count
   * this" is useless unless it also says what failed, and making the sentence the value
   * means a tile can never be marked unknown without one.
   */
  unknown?: string | false | null;
}

export type StatTileProps = StatTileBase;

/**
 * Tone for a problem counter: quiet at zero, loud above it. Keeps "0 failed" from
 * shouting in red next to "3 failed".
 */
export function countTone(count: number, bad: StatTone = 'danger', good: StatTone = 'muted'): StatTone {
  return count > 0 ? bad : good;
}

export function StatTile({
  label,
  value,
  hint,
  tone = 'default',
  glyph,
  to,
  onClick,
  loading = false,
  pending = false,
  terminalBad = false,
  unknown = false,
  title,
}: StatTileProps) {
  const [hover, setHover] = useState(false);
  const isUnknown = Boolean(unknown);
  // An unknown count is never toned as a state — we have not measured one.
  const spec = TONES[isUnknown ? 'muted' : tone];
  const interactive = Boolean(to || onClick);
  const mark = isUnknown ? '?' : glyph === null ? null : (glyph ?? spec.glyph);
  const quiet = isUnknown || tone === 'default' || tone === 'muted';
  // The failure sentence leads: it is the one thing that changes what the tile means. The
  // label rides along because at a narrow width it is the one element that may clip — the
  // VALUE never does, and the label is then still recoverable rather than lost.
  const tooltip = [unknown || null, label, title, hint].filter(Boolean).join(' · ') || undefined;

  const style: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--s-2)',
    minWidth: 0,
    minHeight: '40px',
    textAlign: 'left',
    width: '100%',
    font: 'inherit',
    color: 'var(--text)',
    textDecoration: 'none',
    background: interactive && hover ? 'var(--surface-2)' : 'var(--surface)',
    // Longhand only: React warns (and can render the wrong thing) when a shorthand and a
    // longhand for the same property are both set and one of them changes on a rerender.
    // All four edges the same width — an uneven left edge over a tint is the banned bar.
    borderStyle: isUnknown ? 'solid' : pending ? 'dashed' : terminalBad ? 'double' : 'solid',
    borderWidth: !isUnknown && terminalBad ? '3px' : '1px',
    borderColor: quiet ? 'var(--line)' : spec.line,
    borderRadius: 'var(--r-3)',
    padding: '0 var(--s-3)',
    cursor: interactive ? 'pointer' : 'default',
    transition: 'background var(--dur-1) var(--ease)',
  };

  const body = (
    <>
      {mark ? (
        <span
          aria-hidden="true"
          style={{
            flex: '0 0 auto',
            fontFamily: 'var(--font-mono)',
            color: spec.ink,
            background: spec.soft,
            border: `1px solid ${spec.line}`,
            borderRadius: 'var(--r-1)',
            lineHeight: 1,
            padding: '2px 4px',
            fontSize: 'var(--fs-xs)',
          }}
        >
          {mark}
        </span>
      ) : null}

      <span
        className="truncate"
        style={{
          fontSize: 'var(--fs-xs)',
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: 'var(--text-3)',
        }}
      >
        {label}
      </span>

      <span
        style={{
          marginLeft: 'auto',
          flex: '0 0 auto',
          fontFamily: 'var(--font-mono)',
          fontVariantNumeric: 'tabular-nums',
          fontSize: 'var(--fs-lg)',
          lineHeight: 1.1,
          fontWeight: 600,
          color: isUnknown ? 'var(--text-3)' : tone === 'default' ? 'var(--text)' : spec.ink,
        }}
      >
        {loading ? (
          <span
            className="table-skeleton-bar"
            aria-hidden="true"
            style={{ display: 'inline-block', width: '3.5ch', height: '14px', borderRadius: 'var(--r-1)' }}
          />
        ) : isUnknown ? (
          '—'
        ) : (
          value
        )}
      </span>

      {interactive ? (
        <span aria-hidden="true" style={{ flex: '0 0 auto', color: 'var(--text-3)' }}>
          ›
        </span>
      ) : null}
    </>
  );

  const hoverHandlers = {
    onMouseEnter: () => setHover(true),
    onMouseLeave: () => setHover(false),
    onFocus: () => setHover(true),
    onBlur: () => setHover(false),
  };

  if (to) {
    return (
      <Link to={to} style={style} title={tooltip} {...hoverHandlers}>
        {body}
      </Link>
    );
  }
  if (onClick) {
    return (
      <button type="button" onClick={onClick} style={style} title={tooltip} {...hoverHandlers}>
        {body}
      </button>
    );
  }
  return (
    <div style={style} title={tooltip}>
      {body}
    </div>
  );
}

/**
 * Responsive tile strip. `auto-fit` + `minmax` rather than a media query, so the row
 * reflows at any container width — including the 400px phone case the shell requires.
 * At 150px a full strip fits one row on a 1280px laptop, which is the point: the table
 * that says what is wrong stays above the fold.
 */
export function StatGrid({
  children,
  min = '150px',
  className = '',
}: {
  children: ReactNode;
  /** Narrowest a tile may get before the row wraps. */
  min?: string;
  className?: string;
}) {
  return (
    <div
      className={className}
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(auto-fit, minmax(${min}, 1fr))`,
        gap: 'var(--s-2)',
      }}
    >
      {children}
    </div>
  );
}

export default StatTile;
